/**
 * 週替わりチャレンジ(docs/12_challenge_ranking.md §1)。
 *
 * **問題の並び・採点・報酬・順位は全部サーバー**(Cloud Functions)が持つ。
 * クライアントがやるのは
 *   1. `getChallenge` で「その週の問題の設計図」を受け取り
 *   2. `@paltyping/puzzle` の `generate(genre, level, seed)` で盤面を描き
 *   3. 解答をそのまま `submitChallenge` に送る
 * だけ。**スコアの式は1つも持たない**(改ざん耐性。docs/12 §1)。
 *
 * ローカルモード(Firebase 未設定)には送信先が無いので `NoChallengeService` が
 * `supported: false` を返し、**画面はチャレンジのカードを丸ごと出さない**。
 *
 * ここは `factory.ts` から静的に読まれるので、`@paltyping/puzzle` は
 * **型としてだけ**参照する(`import type`)。値を import するとパズルの
 * 遅延読込チャンクが初期バンドルに載ってしまう(docs/11 §4)。
 */
import type { PuzzleGenre } from '@paltyping/puzzle';

/**
 * 出題1問ぶんの設計図。
 *
 * 出題は**無制限**(docs/12 §1。2026-08-21 改訂)。サーバーは先頭のぶんだけ
 * 先渡しし、その先は `weekId` のシードから**クライアントが同じ規則で作る**
 * (`@paltyping/puzzle` の `challengeBlueprintAt`)。採点のときサーバーも
 * まったく同じ規則で作り直すので、自分で作った問題でもごまかせない。
 */
export interface ChallengeQuestionDef {
  /** 出題順(0始まり)。解答を送るときの目印。 */
  readonly index: number;
  readonly genre: PuzzleGenre;
  readonly level: number;
  readonly seed: number;
  /**
   * 難易度重み(スコアの基礎点)。**サーバーが先渡しした問題にだけ付く**。
   * 画面には出さないし、クライアントは自分で計算しない(改ざん耐性)。
   */
  readonly weight?: number;
}

/**
 * 解答の生の中身。盤面の種類ごとに形が違う判別可能ユニオン。
 *
 *   `choice` = 4択・数字パレット / `cells` = マスの並び /
 *   `grid`   = マスに入れた数     / `marks` = ○×グリッド
 */
export type ChallengeAnswerValue =
  | { readonly kind: 'choice'; readonly value: number | null }
  | { readonly kind: 'cells'; readonly cells: readonly number[] }
  | { readonly kind: 'grid'; readonly grid: readonly (number | null)[] }
  | { readonly kind: 'marks'; readonly marks: readonly ('circle' | 'cross' | null)[] };

/** 自分の参加状態(未挑戦なら `null`)。 */
export interface ChallengeEntry {
  readonly submitted: boolean;
  readonly score: number;
  readonly correct: number;
  readonly answered: number;
  readonly trophyEarned: number;
  /** 年齢帯(1歳ごと)。生年月が未設定なら `null` =「ぜんたい」だけ。 */
  readonly ageBand: number | null;
}

/** getChallenge の応答。 */
export interface ChallengeView {
  readonly weekId: string;
  readonly startsAt: number;
  readonly endsAt: number;
  /** 持ち時間(ms)。**ハード3分**(docs/12 §1)。 */
  readonly durationMs: number;
  /** 先渡しぶんの設計図(出題そのものは無制限)。 */
  readonly questions: readonly ChallengeQuestionDef[];
  /**
   * その週のシード。これがあれば先渡しの続きを自分で作れる。
   * 古いサーバー(先渡しだけの頃)は返さないので `undefined` になりうる。
   */
  readonly seed?: number;
  /** 送れる解答数の上限(サーバーの安全弁)。未指定なら先渡しぶんまで。 */
  readonly maxQuestions?: number;
  readonly entry: ChallengeEntry | null;
  readonly birthYearMonth: string | null;
}

/** 1問ぶんの解答。 */
export interface ChallengeAnswer {
  readonly index: number;
  readonly answer: ChallengeAnswerValue;
  readonly elapsedMs: number;
}

export interface ChallengeSubmission {
  readonly weekId: string;
  readonly answers: readonly ChallengeAnswer[];
}

/** submitChallenge の応答。 */
export interface ChallengeReceipt {
  readonly weekId: string;
  readonly score: number;
  readonly correct: number;
  readonly answered: number;
  /** 3分を超えて無効になった解答の数。 */
  readonly expired: number;
  readonly trophyEarned: number;
  /** 付与後のトロフィーコイン残高。 */
  readonly balance: number;
  readonly ageBand: number | null;
  readonly rank: number;
  readonly total: number;
  /** 上位何%(1〜100)。**画面の主役はこれ**(docs/12 §1)。 */
  readonly topPercent: number;
  /** 今回もらったがんばりポイント(docs/12 §2。リーグの順位に効く)。 */
  readonly effortEarned: number;
  /** 今週のがんばりポイント合計。 */
  readonly effortWeekTotal: number;
}

export interface LeaderboardRow {
  readonly rank: number;
  readonly displayName: string;
  readonly score: number;
  readonly ageBand: number | null;
  readonly self: boolean;
}

export interface LeaderboardView {
  readonly weekId: string;
  readonly band: 'all' | number;
  readonly top: readonly LeaderboardRow[];
  readonly total: number;
  readonly self: LeaderboardRow | null;
  readonly topPercent: number | null;
}

/** ランキング用の最小プロフィール(docs/12 §1「生まれ年+月のみ」)。 */
export interface ChallengeProfile {
  readonly birthYearMonth: string | null;
  readonly displayName: string | null;
}

/**
 * チャレンジの窓口。
 *
 * `supported: false` のときは**画面に導線を出さない**(ローカルモード)。
 * 通信できなかったメソッドは `null` を返す。**画面はそれで止まらない**。
 */
export interface ChallengeService {
  readonly supported: boolean;
  /** 今週のチャレンジ定義と自分の参加状態。 */
  load(): Promise<ChallengeView | null>;
  /** 解答を送って採点結果を受け取る(週1回。2回目はサーバーが拒否する)。 */
  submit(input: ChallengeSubmission): Promise<ChallengeReceipt | null>;
  /** その週×年齢帯のランキング。 */
  leaderboard(weekId: string, band: 'all' | number): Promise<LeaderboardView | null>;
  /** 生年月・ニックネームの保存(省略したフィールドは触らない)。 */
  saveProfile(input: Partial<ChallengeProfile>): Promise<ChallengeProfile | null>;
}

/**
 * ローカルモード用の何もしない実装。
 *
 * チャレンジは**サーバー採点が前提**(docs/12 §1)なので、
 * ローカルモードでは遊べない。カードごと隠すのが正しい振る舞い
 * (「あそべません」と出して期待させない)。
 */
export class NoChallengeService implements ChallengeService {
  readonly supported = false;

  async load(): Promise<ChallengeView | null> {
    return null;
  }

  async submit(): Promise<ChallengeReceipt | null> {
    return null;
  }

  async leaderboard(): Promise<LeaderboardView | null> {
    return null;
  }

  async saveProfile(): Promise<ChallengeProfile | null> {
    return null;
  }
}
